// Vendor Libs
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

// Material UI v5
import { Autocomplete, TextField, Chip } from '@mui/material';

// Core Libs
import { formatDate } from '../../lib/core/date';

// Containers
import withFormField from '../../containers/with-form-field';

// Styles
import './chip-input.scss';

const MAX_LENGTH = 10;

function ChipsInput({
  className,
  disableAutofill,
  disabled,
  id,
  name,
  onBlur,
  onChange,
  onClick,
  placeholder = 'Add new call sign and press return',
  size,
  type,
  value,
}) {
  const [chips, setChips] = useState(Array.isArray(value) ? value : []);
  const [error, setError] = useState('');

  useEffect(() => {
    setChips(Array.isArray(value) ? value : []);
    setError('');
  }, [value]);

  const handleChange = (event, newValue) => {
    // Normalize to objects
    const normalized = newValue.map(item =>
      typeof item === 'string' ? { callSignValue: item } : item
    );
    const unique = [];
    let err = '';

    normalized.forEach(chip => {
      if (unique.find(u => u.callSignValue === chip.callSignValue)) {
        return;
      }
      if (chip.callSignValue.length >= MAX_LENGTH) {
        err = `Call Sign value should be less than ${MAX_LENGTH} characters`;
      } else {
        unique.push(chip);
      }
    });

    setChips(unique);
    setError(err);
    onChange(null, unique);
  };

  // Fallback for non-array types
  if (type !== 'array') {
    const inputProps = {};
    if (type === 'datetime') {
      inputProps.type = 'date';
      inputProps.value = formatDate(value, 'YYYY-MM-DD') || '';
    } else if (type === 'number') {
      inputProps.type = 'number';
      inputProps.inputProps = { min: 0, step: 1 };
      inputProps.value = value;
    } else {
      inputProps.type = type;
      inputProps.value = value;
    }

    return (
      <TextField
          variant="outlined"
          {...inputProps}
          id={id}
          name={disableAutofill ? undefined : name}
          autoComplete={disableAutofill ? 'new-password' : undefined}
          disabled={disabled}
          onBlur={onBlur}
          onClick={onClick}
          placeholder={placeholder}
          error={!!error}
          helperText={error}
          size={size}
      />
    );
  }

  // Array type: chips input
  return (
    <>
      <Autocomplete
          multiple={true}
          freeSolo={true}
          options={[]}
          className={classnames('chips-input', className)}
          value={chips}
          onChange={handleChange}
          disabled={disabled}
          getOptionLabel={option => option.callSignValue || ''}
          filterSelectedOptions={true}
          renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
                key={option.callSignValue}
                label={option.callSignValue}
                {...getTagProps({ index })}
            />
          ))}
          renderInput={params => (
          <TextField
              {...params}
              variant="outlined"
              id={id}
              name={disableAutofill ? undefined : name}
              autoComplete={disableAutofill ? 'new-password' : undefined}
              onBlur={onBlur}
              onClick={onClick}
              placeholder={placeholder}
              error={!!error}
              helperText={error}
              size={size}
              onKeyPress={e => e.stopPropagation()}
          />
        )}
      />
    </>
  );
}

ChipsInput.propTypes = {
  className: PropTypes.string,
  disableAutofill: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  type: PropTypes.string,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      callSignValue: PropTypes.string.isRequired,
    })
  ).isRequired,
};

ChipsInput.defaultProps = {
  type: 'array',
};

export default withFormField(ChipsInput);
